﻿import React, { useState, useEffect, useRef } from 'react'
import { PermissionGuard } from '../components/PermissionGuard'
import { usePermission } from '../hooks/usePermissions'
import { Heading, Text, Button, Input, Select, LoadingSpinner, TagInput, Editor, Tooltip } from '../components/ui'
import { BRAND_COLOR } from '../constants'
import { 
  listArticles, 
  createArticle, 
  updateArticle, 
  deleteArticle,
  listCategories,
  listDepartments,
  listTags,
  searchArticles,
  getMultipleUsersInfo,
  getAllUsers,
  updateArticlesWithoutUser,
  getArticleVersions,
  restoreArticleVersion,
  addFavorite,
  removeFavorite,
  isFavorite,
  getFavoriteArticleIds,
  listFavorites,
  duplicateArticle,
  trackArticleView,
  getWikiStats,
  getArticleStats
} from '../services/wiki'
import VersionDiff from '../components/VersionDiff'
import { WikiArticle, WikiCategory, WikiDepartment, WikiTag, CreateArticlePayload } from '../types/wiki'
import {
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  DocumentTextIcon,
  TagIcon,
  FolderIcon,
  CalendarIcon,
  UserIcon,
  CheckIcon,
  XMarkIcon,
  EyeIcon,
  ClockIcon,
  FunnelIcon,
  AdjustmentsHorizontalIcon,
  XCircleIcon,
  StarIcon,
  HeartIcon,
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'
import {
  StarIcon as StarIconSolid,
  HeartIcon as HeartIconSolid
} from '@heroicons/react/24/solid'

const WikiArtigos: React.FC = () => {
  // Hooks de permissões
  const { hasPermission: canInsert } = usePermission('wiki_artigos', 'insert')
  const { hasPermission: canUpdate } = usePermission('wiki_artigos', 'update')
  const { hasPermission: canDelete } = usePermission('wiki_artigos', 'delete')
  
  // Estados principais
  const [articles, setArticles] = useState<WikiArticle[]>([])
  const [categories, setCategories] = useState<WikiCategory[]>([])
  const [departments, setDepartments] = useState<WikiDepartment[]>([])
  const [tags, setTags] = useState<WikiTag[]>([])
  const [authors, setAuthors] = useState<Array<{id: string, name: string}>>([])
  const [responsibles, setResponsibles] = useState<Array<{id: string, name: string}>>([])
  const [allUsers, setAllUsers] = useState<Array<{id: string, name: string}>>([])
  const [userNames, setUserNames] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)

  // Estados de filtros e busca
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedDepartment, setSelectedDepartment] = useState('')
  const [selectedTag, setSelectedTag] = useState('')
  const [selectedAuthor, setSelectedAuthor] = useState('')
  const [selectedResponsible, setSelectedResponsible] = useState('')
  const [statusFilter, setStatusFilter] = useState<'draft' | 'published' | 'archived' | ''>('')
  const [sortBy, setSortBy] = useState('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm)

  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(25)
  const [totalItems, setTotalItems] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  // Estados de modais
  const [showModal, setShowModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showVersionsModal, setShowVersionsModal] = useState(false)
  const [showVersionViewModal, setShowVersionViewModal] = useState(false)
  const [showVersionDiffModal, setShowVersionDiffModal] = useState(false)
  const [showInternalLinkHelper, setShowInternalLinkHelper] = useState(false)
  const [isViewMaximized, setIsViewMaximized] = useState(false)

  // Estados de artigos
  const [editingArticle, setEditingArticle] = useState<WikiArticle | null>(null)
  const [viewingArticle, setViewingArticle] = useState<WikiArticle | null>(null)
  const [versionsArticle, setVersionsArticle] = useState<WikiArticle | null>(null)
  
  // Estados de validação
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [viewingVersion, setViewingVersion] = useState<any>(null)
  const [versions, setVersions] = useState<any[]>([])
  const [versionsLoading, setVersionsLoading] = useState(false)

  // Estados de formulário
  const [formData, setFormData] = useState<CreateArticlePayload>({
    title: '',
    body: '',
    category_id: '',
    department_id: '',
    responsible_id: '',
    status: 'draft' as 'draft' | 'published' | 'archived',
    tags: []
  })
  const [modalLoading, setModalLoading] = useState(false)

  // Estados de favoritos
  const [favoriteIds, setFavoriteIds] = useState<string[]>([])
  const [favorites, setFavorites] = useState<WikiArticle[]>([])
  const [showFavorites, setShowFavorites] = useState(false)

  // Estados de estatísticas
  const [stats, setStats] = useState<any>(null)

  // Estado para controlar visibilidade dos filtros
  const [filtersExpanded, setFiltersExpanded] = useState(false)


  // Função para buscar dados
  const fetchData = async (opts?: { showOverlay?: boolean, query?: string }) => {
    try {
      const showOverlay = opts?.showOverlay ?? true
      if (showOverlay) {
        setLoading(true)
      }
      setError(null)

      const [articlesData, categoriesData, departmentsData, tagsData, statsData, allUsersData] = await Promise.all([
        listArticles({
          query: opts?.query ?? searchTerm,
          category_id: selectedCategory,
          department_id: selectedDepartment,
          tag_ids: selectedTag ? [selectedTag] : undefined,
          created_by: selectedAuthor || undefined,
          responsible_id: selectedResponsible || undefined,
          status: statusFilter || undefined,
          sort_by: sortBy,
          sort_order: sortOrder
        }, currentPage, itemsPerPage),
        listCategories(),
        listDepartments(),
        listTags(),
        getWikiStats(),
        getAllUsers()
      ])

      setArticles(articlesData.articles)
      setTotalItems(articlesData.total)
      setTotalPages(Math.ceil(articlesData.total / itemsPerPage))
      setCategories(categoriesData)
      setDepartments(departmentsData)
      setTags(tagsData)
      setStats(statsData)
      setAllUsers(allUsersData)

      // Buscar nomes dos usuários (autores e responsáveis)
      const authorIds = Array.from(new Set(articlesData.articles.map(article => article.created_by).filter(Boolean))) as string[]
      const responsibleIds = Array.from(new Set(articlesData.articles.map(article => article.responsible_id).filter(Boolean))) as string[]
      const allUserIds = Array.from(new Set([...authorIds, ...responsibleIds]))
      
      if (allUserIds.length > 0) {
        const usersData = await getMultipleUsersInfo(allUserIds)
        const userNamesMap: Record<string, string> = {}
        const authorsList: Array<{id: string, name: string}> = []
        const responsiblesList: Array<{id: string, name: string}> = []
        
        Object.entries(usersData).forEach(([id, user]) => {
          userNamesMap[id] = user.name
          // Adicionar à lista de autores se for autor de algum artigo
          if (authorIds.includes(id)) {
            authorsList.push({ id, name: user.name })
          }
          // Adicionar à lista de responsáveis se for responsável de algum artigo
          if (responsibleIds.includes(id)) {
            responsiblesList.push({ id, name: user.name })
          }
        })
        setUserNames(userNamesMap)
        setAuthors(authorsList.sort((a, b) => a.name.localeCompare(b.name)))
        // Criar estado para responsáveis separado dos autores
        setResponsibles(responsiblesList.sort((a, b) => a.name.localeCompare(b.name)))
      }

      setRetryCount(0)
      // Atualizar favoritos após carregar artigos
      try {
        const ids = await getFavoriteArticleIds()
        setFavoriteIds(ids)
        const favs = await listFavorites()
        const favArticles = (favs || [])
          .map((f: any) => f.article)
          .filter(Boolean)
        setFavorites(favArticles)
      } catch (e) {
        console.error('Erro ao carregar favoritos:', e)
      }
    } catch (err) {
      console.error('Erro ao carregar dados:', err)
      setError(err instanceof Error ? err.message : 'Erro desconhecido')
      setRetryCount(prev => prev + 1)
    } finally {
      setLoading(false)
    }
  }

  // Função de retry
  const retryFetch = () => {
    if (retryCount < 3) {
      fetchData()
    }
  }

  // Debounce do termo de busca
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchTerm(searchTerm), 400)
    return () => clearTimeout(handler)
  }, [searchTerm])

  // Resetar página para 1 quando filtros mudarem (exceto mudanças de página)
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearchTerm, selectedCategory, selectedDepartment, selectedTag, selectedAuthor, selectedResponsible, statusFilter, sortBy, sortOrder])

  // Evitar overlay durante mudanças de filtros: buscar quando qualquer filtro muda
  const didMountRef = useRef(false)
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true
      // Primeira carga com overlay
      fetchData({ showOverlay: true, query: debouncedSearchTerm })
      return
    }
    // Atualizações por mudança de filtros sem overlay
    fetchData({ showOverlay: false, query: debouncedSearchTerm })
  }, [debouncedSearchTerm, currentPage, itemsPerPage, selectedCategory, selectedDepartment, selectedTag, selectedAuthor, selectedResponsible, statusFilter, sortBy, sortOrder])

  // Funções de modal
  const openModal = (article?: WikiArticle) => {
    // Verificar permissões
    if (article) {
      // Editando artigo - precisa de permissão update
      if (!canUpdate) {
        alert('Você não tem permissão para editar artigos.')
        return
      }
    } else {
      // Criando artigo - precisa de permissão insert
      if (!canInsert) {
        alert('Você não tem permissão para criar artigos.')
        return
      }
    }

    if (article) {
      setEditingArticle(article)
    setFormData({
      title: article.title,
      body: article.body,
      category_id: article.category_id || '',
      department_id: article.department_id || '',
      responsible_id: article.responsible_id || '',
      status: article.status,
      tags: (article.tags || []).map(tag => tag.name)
    })
    } else {
      setEditingArticle(null)
      setFormData({
        title: '',
        body: '',
        category_id: '',
        department_id: '',
        responsible_id: '',
        status: 'draft',
        tags: []
      })
    }
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingArticle(null)
    setValidationErrors({})
    setFormData({
      title: '',
      body: '',
      category_id: '',
      department_id: '',
      responsible_id: '',
      status: 'draft' as 'draft' | 'published' | 'archived',
      tags: []
    })
  }

  const openViewModal = (article: WikiArticle) => {
    setViewingArticle(article)
    setShowViewModal(true)
    trackArticleView(article.id)
    setIsViewMaximized(false)
  }

  const closeViewModal = () => {
    setShowViewModal(false)
    setViewingArticle(null)
    setIsViewMaximized(false)
  }

  const openVersionsModal = async (article: WikiArticle) => {
    setVersionsArticle(article)
    setShowVersionsModal(true)
    setVersionsLoading(true)
    
    try {
      const versionsData = await getArticleVersions(article.id)
      setVersions(versionsData)
    } catch (err) {
      console.error('Erro ao carregar versões:', err)
    } finally {
      setVersionsLoading(false)
    }
  }

  const closeVersionsModal = () => {
    setShowVersionsModal(false)
    setVersionsArticle(null)
    setVersions([])
  }

  const openVersionViewModal = (version: any) => {
    setViewingVersion(version)
    setShowVersionViewModal(true)
  }

  const closeVersionViewModal = () => {
    setShowVersionViewModal(false)
    setViewingVersion(null)
  }

  const openVersionDiffModal = () => {
    setShowVersionDiffModal(true)
  }

  const closeVersionDiffModal = () => {
    setShowVersionDiffModal(false)
  }

  // Função de validação
  const validateForm = () => {
    const errors: Record<string, string> = {}
    
    // Validar título
    if (!formData.title || formData.title.trim() === '') {
      errors.title = 'Título é obrigatório'
    }
    
    // Validar categoria
    if (!formData.category_id || formData.category_id === '') {
      errors.category_id = 'Categoria é obrigatória'
    }
    
    // Validar departamento
    if (!formData.department_id || formData.department_id === '') {
      errors.department_id = 'Departamento é obrigatório'
    }
    
    // Validar responsável
    if (!formData.responsible_id || formData.responsible_id === '') {
      errors.responsible_id = 'Responsável é obrigatório'
    }
    
    // Validar status
    if (!formData.status) {
      errors.status = 'Status é obrigatório'
    }
    
    // Validar conteúdo
    if (!formData.body || formData.body.trim() === '') {
      errors.body = 'Conteúdo é obrigatório'
    }
    
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Funções de CRUD
  const handleSave = async () => {
    // Validar formulário antes de salvar
    if (!validateForm()) {
      return
    }
    
    try {
      setModalLoading(true)
      
      // Preparar dados para envio - converter strings vazias para undefined
      const payload: CreateArticlePayload = {
        title: formData.title.trim(),
        body: formData.body.trim(),
        category_id: formData.category_id || undefined,
        department_id: formData.department_id || undefined,
        responsible_id: formData.responsible_id,
        status: formData.status,
        tags: formData.tags || []
      }
      
      if (editingArticle) {
        await updateArticle({ id: editingArticle.id, ...payload })
      } else {
        await createArticle(payload)
      }
      
      await fetchData()
      closeModal()
    } catch (err) {
      console.error('Erro ao salvar artigo:', err)
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido ao salvar o artigo'
      alert(`Erro ao salvar artigo: ${errorMessage}`)
    } finally {
      setModalLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    // Verificar permissão para excluir
    if (!canDelete) {
      alert('Você não tem permissão para excluir artigos.')
      return
    }

    if (window.confirm('Tem certeza que deseja excluir este artigo?')) {
      try {
        await deleteArticle(id)
        await fetchData()
      } catch (err) {
        console.error('Erro ao excluir artigo:', err)
      }
    }
  }

  const handleDuplicate = async (id: string) => {
    // Verificar permissão para duplicar (criar novo artigo)
    if (!canInsert) {
      alert('Você não tem permissão para duplicar artigos.')
      return
    }

    try {
      await duplicateArticle(id)
      await fetchData()
    } catch (err) {
      console.error('Erro ao duplicar artigo:', err)
    }
  }

  const handleRestoreVersion = async (versionNumber: number) => {
    // Verificar permissão para restaurar (editar artigo)
    if (!canUpdate) {
      alert('Você não tem permissão para restaurar versões.')
      return
    }

    if (versionsArticle && window.confirm('Tem certeza que deseja restaurar esta versão?')) {
      try {
        await restoreArticleVersion(versionsArticle.id, versionNumber)
        await fetchData()
        closeVersionsModal()
      } catch (err) {
        console.error('Erro ao restaurar versão:', err)
      }
    }
  }

  // Funções de favoritos
  const handleToggleFavorite = async (articleId: string) => {
    try {
      const isFav = favoriteIds.includes(articleId)
      if (isFav) {
        await removeFavorite(articleId)
      } else {
        await addFavorite(articleId)
      }
      
      const newFavoriteIds = await getFavoriteArticleIds()
      setFavoriteIds(newFavoriteIds)
    } catch (err) {
      console.error('Erro ao alterar favorito:', err)
    }
  }

  // Limpar filtros
  const handleClearFilters = () => {
    setSearchTerm('')
    setDebouncedSearchTerm('')
    setSelectedCategory('')
    setSelectedDepartment('')
    setSelectedTag('')
    setSelectedAuthor('')
    setSelectedResponsible('')
    setStatusFilter('')
    setSortBy('created_at')
    setSortOrder('desc')
    setShowFavorites(false)
    setCurrentPage(1)
  }

  // Funções de paginação
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagesCount) {
      setCurrentPage(page)
    }
  }

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
  }

  // Função para processar links internos
  const processInternalLinks = (content: string, articles: WikiArticle[]) => {
    return content.replace(/\[\[([^\]]+)\]\]/g, (match, title) => {
      const article = articles.find(a => a.title === title)
      if (article) {
        return `<a href="#" onclick="event.preventDefault(); window.openArticle('${article.id}')" class="text-primary-600 hover:text-primary-800 underline">${title}</a>`
      }
      return match
    })
  }

  // Artigos para exibição (com suporte ao filtro de favoritos)
  const displayArticles = showFavorites ? favorites : articles
  
  // Aplicar filtros de busca nos favoritos também
  const filteredDisplayArticles = showFavorites 
    ? displayArticles.filter(article => {
        if (!debouncedSearchTerm) return true
        const searchLower = debouncedSearchTerm.toLowerCase()
        return article.title.toLowerCase().includes(searchLower) || 
               article.body.toLowerCase().includes(searchLower)
      })
    : displayArticles
  
  // Determinar se há filtros aplicados
  const hasFilters = debouncedSearchTerm || selectedCategory || selectedDepartment || selectedTag || selectedAuthor || selectedResponsible || statusFilter || showFavorites
  
  // Calcular valores de paginação baseados nos resultados filtrados
  const resultsCount = hasFilters ? filteredDisplayArticles.length : totalItems
  const pagesCount = hasFilters ? Math.ceil(filteredDisplayArticles.length / itemsPerPage) : totalPages
  
  // Aplicar paginação aos artigos filtrados
  const paginatedArticles = hasFilters 
    ? filteredDisplayArticles.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    : articles
  
  const showingStart = resultsCount > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0
  const showingEnd = Math.min(currentPage * itemsPerPage, resultsCount)

  // Ajustar página atual se ela for maior que o número total de páginas
  useEffect(() => {
    if (pagesCount > 0 && currentPage > pagesCount) {
      setCurrentPage(1)
    }
  }, [pagesCount, currentPage])


  return (
    <div className="h-full flex flex-col overflow-hidden">
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <LoadingSpinner size="lg" className="mx-auto mb-4" />
              <Text size="lg" className="text-gray-600 dark:text-gray-400 mb-2">
                Carregando Artigos da Wiki...
              </Text>
              {retryCount > 0 && (
                <Text size="sm" className="text-gray-500 dark:text-gray-500">
                  Tentativa {retryCount} de 3...
                </Text>
              )}
            </div>
          </div>
        ) : error ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center max-w-md">
              <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <Text size="lg" className="text-red-600 dark:text-red-400 mb-2">
                Erro ao carregar Artigos da Wiki
              </Text>
              <Text size="sm" className="text-gray-600 dark:text-gray-400 mb-4">
                {error}
              </Text>
              <Button
                onClick={retryFetch}
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 mx-auto"
              >
                <ArrowPathIcon className="h-4 w-4" />
                Tentar Novamente
              </Button>
              {retryCount > 0 && (
                <Text size="xs" className="text-gray-500 dark:text-gray-500 mt-2">
                  Já tentamos {retryCount} vezes
                </Text>
              )}
            </div>
          </div>
        ) : (
          <PermissionGuard 
            moduleName="wiki_artigos" 
            permissionType="show"
            fallback={
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                    Acesso Negado
                  </Text>
                  <Text size="sm" color="muted">
                    Você não tem permissão para acessar os Artigos da Wiki.
                  </Text>
                </div>
              </div>
            }
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                    Artigos da Wiki
                  </Heading>
                  {stats && (
                    <Text size="sm" color="muted" className="mt-2">
                      {stats.total_articles} artigos • {stats.total_categories} categorias • {stats.total_tags} tags
                    </Text>
                  )}
                </div>
                <Button
                  onClick={() => openModal()}
                  className="flex items-center gap-2"
                  style={{ backgroundColor: BRAND_COLOR }}
                >
                  <PlusIcon className="h-5 w-5" />
                  Novo Artigo
                </Button>
              </div>
            </div>

            {/* Filtros */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              {/* Header dos filtros com botão de toggle */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <Button
                    onClick={() => { setShowFavorites(!showFavorites); setCurrentPage(1) }}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-1"
                    aria-pressed={showFavorites}
                  >
                    {showFavorites ? (
                      <HeartIconSolid className="h-4 w-4 text-red-500" />
                    ) : (
                      <HeartIcon className="h-4 w-4" />
                    )}
                  </Button>
                  
                  <div className="flex items-center gap-2">
                    <Text size="sm">Ordenar por:</Text>
                    <Select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="w-40"
                    >
                      <option value="created_at">Data</option>
                      <option value="title">Título</option>
                      <option value="status">Status</option>
                    </Select>
                    <Button
                      onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                      variant="outline"
                      size="sm"
                    >
                      {sortOrder === 'asc' ? '↑' : '↓'}
                    </Button>
                    <Button
                      onClick={handleClearFilters}
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-1"
                    >
                      <XCircleIcon className="h-4 w-4" />
                      Limpar filtros
                    </Button>
                  </div>
                </div>
                
                <Button
                  onClick={() => setFiltersExpanded(!filtersExpanded)}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <FunnelIcon className="h-4 w-4" />
                  {filtersExpanded ? 'Ocultar Filtros' : 'Mostrar Filtros'}
                </Button>
              </div>

              {/* Filtros expandíveis */}
              <div className={`transition-all duration-300 ease-in-out ${
                filtersExpanded
                  ? 'max-h-96 opacity-100 mt-4 overflow-visible'
                  : 'max-h-0 opacity-0 overflow-hidden pointer-events-none'
              }`}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 py-0.5">
                  <div>
                    <Text size="sm" className="mb-2">Buscar</Text>
                    <div className="relative">
                      <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <Input
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Buscar artigos..."
                        className="pl-10"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Text size="sm" className="mb-2">Categoria</Text>
                    <Select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                      <option value="">Todas as categorias</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                  
                  <div>
                    <Text size="sm" className="mb-2">Departamento</Text>
                    <Select
                      value={selectedDepartment}
                      onChange={(e) => setSelectedDepartment(e.target.value)}
                    >
                      <option value="">Todos os departamentos</option>
                      {departments.map(department => (
                        <option key={department.id} value={department.id}>
                          {department.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                  
                  <div>
                    <Text size="sm" className="mb-2">Autor</Text>
                    <Select
                      value={selectedAuthor}
                      onChange={(e) => setSelectedAuthor(e.target.value)}
                    >
                      <option value="">Todos os autores</option>
                      {authors.map(author => (
                        <option key={author.id} value={author.id}>
                          {author.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                  
                  <div>
                    <Text size="sm" className="mb-2">Responsável</Text>
                    <Select
                      value={selectedResponsible}
                      onChange={(e) => setSelectedResponsible(e.target.value)}
                    >
                      <option value="">Todos os responsáveis</option>
                      {responsibles.map(responsible => (
                        <option key={responsible.id} value={responsible.id}>
                          {responsible.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                  
                  <div>
                    <Text size="sm" className="mb-2">Status</Text>
                    <Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value as 'draft' | 'published' | 'archived' | '')}
                    >
                      <option value="">Todos os status</option>
                      <option value="published">Publicado</option>
                      <option value="draft">Rascunho</option>
                      <option value="archived">Arquivado</option>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            {/* Lista de Artigos */}
            <div className="flex-1 overflow-y-auto p-6">
              {paginatedArticles.length === 0 ? (
                <div className="text-center py-12">
                  <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <Text size="lg" className="text-gray-500 mb-2">
                    Nenhum artigo encontrado
                  </Text>
                  <Text size="sm" className="text-gray-400">
                    Tente ajustar os filtros ou criar um novo artigo
                  </Text>
                </div>
              ) : (
                <div className="grid gap-4">
                  {paginatedArticles.map((article) => (
                    <div key={article.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                              {article.title}
                            </Heading>
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              article.status === 'published' 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : article.status === 'draft'
                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                            }`}>
                              {article.status === 'published' ? 'Publicado' : 
                               article.status === 'draft' ? 'Rascunho' : 'Arquivado'}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-3">
                            {article.category && (
                              <div className="flex items-center gap-1">
                                <FolderIcon className="h-4 w-4" />
                                {article.category.name}
                              </div>
                            )}
                            {article.department && (
                              <div className="flex items-center gap-1">
                                <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs">
                                  {article.department.name}
                                </span>
                              </div>
                            )}
                            <div className="flex items-center gap-1">
                              <CalendarIcon className="h-4 w-4" />
                              {new Date(article.created_at).toLocaleDateString('pt-BR')}
                            </div>
                            <div className="flex items-center gap-1">
                              <UserIcon className="h-4 w-4" />
                              {userNames[article.created_by || ''] || article.created_by || 'Usuário'}
                            </div>
                            {article.responsible_id && (
                              <div className="flex items-center gap-1">
                                <UserIcon className="h-4 w-4" />
                                <span className="text-xs text-gray-500">Resp:</span>
                                {userNames[article.responsible_id] || article.responsible_id || 'Usuário'}
                              </div>
                            )}
                          </div>
                          
                          {article.tags && article.tags.length > 0 && (
                            <div className="flex items-center gap-2 mb-3">
                              {article.tags.map((tag) => (
                                <span
                                  key={tag.id}
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 rounded-full text-xs"
                                >
                                  <TagIcon className="h-3 w-3" />
                                  {tag.name}
                                </span>
                              ))}
                            </div>
                          )}
                          
                          <div className="text-gray-600 dark:text-gray-300 line-clamp-2">
                            {article.body.replace(/<[^>]*>/g, '').substring(0, 200)}...
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 ml-4">
                          <Tooltip content={favoriteIds.includes(article.id) ? "Remover dos favoritos" : "Adicionar aos favoritos"}>
                            <Button
                              onClick={() => handleToggleFavorite(article.id)}
                              size="sm"
                              variant="outline"
                              className="flex items-center gap-1"
                            >
                              {favoriteIds.includes(article.id) ? (
                                <HeartIconSolid className="h-4 w-4 text-red-500" />
                              ) : (
                                <HeartIcon className="h-4 w-4" />
                              )}
                            </Button>
                          </Tooltip>
                          <Tooltip content="Visualizar artigo">
                            <Button
                              onClick={() => openViewModal(article)}
                              size="sm"
                              variant="outline"
                              className="flex items-center gap-1"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Ver histórico de versões">
                            <Button
                              onClick={() => openVersionsModal(article)}
                              size="sm"
                              variant="outline"
                              className="flex items-center gap-1"
                            >
                              <ClockIcon className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Editar artigo">
                            <Button
                              onClick={() => openModal(article)}
                              size="sm"
                              variant="outline"
                              className="flex items-center gap-1"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Duplicar artigo">
                            <Button
                              onClick={() => handleDuplicate(article.id)}
                              size="sm"
                              variant="outline"
                              className="flex items-center gap-1 text-primary-600 hover:text-primary-700"
                            >
                              <DocumentDuplicateIcon className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Excluir artigo">
                            <Button
                              onClick={() => handleDelete(article.id)}
                              size="sm"
                              variant="outline"
                              className="flex items-center gap-1 text-red-600 hover:text-red-700"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Paginação - sempre exibe quando há resultados */}
            {!loading && resultsCount > 0 && (
              <div className="mt-6 w-full">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center gap-4">
                    <Text size="sm" className="text-gray-600 dark:text-gray-400">
                      {hasFilters ? (
                        <>Mostrando {showingStart} a {showingEnd} de {resultsCount} resultados filtrados</>
                      ) : (
                        <>Mostrando {showingStart} a {showingEnd} de {resultsCount} resultados</>
                      )}
                    </Text>
                    <Select
                      value={itemsPerPage.toString()}
                      onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                      className="w-20"
                    >
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </Select>
                    <Text size="sm" className="text-gray-600 dark:text-gray-400">
                      por página
                    </Text>
                  </div>
                  
                  {pagesCount > 1 && (
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        size="sm"
                        variant="outline"
                      >
                        Anterior
                      </Button>
                      
                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, pagesCount) }, (_, i) => {
                          const page = i + 1
                          return (
                            <Button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              size="sm"
                              variant={currentPage === page ? "solid" : "outline"}
                              className={currentPage === page ? "bg-primary-600 text-white" : ""}
                            >
                              {page}
                            </Button>
                          )
                        })}
                        {pagesCount > 5 && (
                          <>
                            <Text size="sm" className="text-gray-500">...</Text>
                            <Button
                              onClick={() => handlePageChange(pagesCount)}
                              size="sm"
                              variant={currentPage === pagesCount ? "solid" : "outline"}
                              className={currentPage === pagesCount ? "bg-primary-600 text-white" : ""}
                            >
                              {pagesCount}
                            </Button>
                          </>
                        )}
                      </div>
                      
                      <Button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === pagesCount}
                        size="sm"
                        variant="outline"
                      >
                        Próxima
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </PermissionGuard>
        )}

      {/* Modal de Edição */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                {editingArticle ? 'Editar Artigo' : 'Novo Artigo'}
              </Heading>
              <Button
                onClick={closeModal}
                variant="outline"
                size="sm"
                className="flex items-center gap-1"
              >
                <XMarkIcon className="h-4 w-4" />
                Fechar
              </Button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <div className="space-y-6">
                {/* Título */}
                <div>
                  <Text size="sm" className="mb-2">
                    Título <span className="text-red-500">*</span>
                  </Text>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Digite o título do artigo..."
                    className={validationErrors.title ? 'border-red-500' : ''}
                  />
                  {validationErrors.title && (
                    <Text size="sm" className="text-red-500 mt-1">
                      {validationErrors.title}
                    </Text>
                  )}
                </div>

                {/* Categoria */}
                <div>
                  <Text size="sm" className="mb-2">
                    Categoria <span className="text-red-500">*</span>
                  </Text>
                  <Select
                    value={formData.category_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, category_id: e.target.value }))}
                    className={validationErrors.category_id ? 'border-red-500' : ''}
                  >
                    <option value="">Selecione uma categoria</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </Select>
                  {validationErrors.category_id && (
                    <Text size="sm" className="text-red-500 mt-1">
                      {validationErrors.category_id}
                    </Text>
                  )}
                </div>

                {/* Departamento */}
                <div>
                  <Text size="sm" className="mb-2">
                    Departamento <span className="text-red-500">*</span>
                  </Text>
                  <Select
                    value={formData.department_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, department_id: e.target.value }))}
                    className={validationErrors.department_id ? 'border-red-500' : ''}
                  >
                    <option value="">Selecione um departamento</option>
                    {departments.map(department => (
                      <option key={department.id} value={department.id}>
                        {department.name}
                      </option>
                    ))}
                  </Select>
                  {validationErrors.department_id && (
                    <Text size="sm" className="text-red-500 mt-1">
                      {validationErrors.department_id}
                    </Text>
                  )}
                </div>

                {/* Responsável */}
                <div>
                  <Text size="sm" className="mb-2">
                    Responsável <span className="text-red-500">*</span>
                  </Text>
                  <Select
                    value={formData.responsible_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, responsible_id: e.target.value }))}
                    className={validationErrors.responsible_id ? 'border-red-500' : ''}
                  >
                    <option value="">Selecione um responsável</option>
                    {allUsers.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </Select>
                  {validationErrors.responsible_id && (
                    <Text size="sm" className="text-red-500 mt-1">
                      {validationErrors.responsible_id}
                    </Text>
                  )}
                </div>

                {/* Status */}
                <div>
                  <Text size="sm" className="mb-2">
                    Status <span className="text-red-500">*</span>
                  </Text>
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as 'draft' | 'published' | 'archived' }))}
                    className={validationErrors.status ? 'border-red-500' : ''}
                  >
                    <option value="draft">Rascunho</option>
                    <option value="published">Publicado</option>
                    <option value="archived">Arquivado</option>
                  </Select>
                  {validationErrors.status && (
                    <Text size="sm" className="text-red-500 mt-1">
                      {validationErrors.status}
                    </Text>
                  )}
                </div>

                {/* Tags com Autocompletar */}
                <TagInput
                  tags={formData.tags}
                  onTagsChange={(newTags) => setFormData(prev => ({ ...prev, tags: newTags }))}
                  availableTags={tags}
                  placeholder="Digite uma tag ou selecione das sugestões..."
                />

                {/* Conteúdo */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Text size="sm">
                      Conteúdo <span className="text-red-500">*</span>
                    </Text>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowInternalLinkHelper(true)}
                      className="flex items-center gap-2"
                    >
                      <DocumentTextIcon className="h-4 w-4" />
                      Inserir Link Interno
                    </Button>
                  </div>
                  <div className={validationErrors.body ? 'border border-red-500 rounded-md' : ''}>
                    <Editor
                      value={formData.body}
                      onChange={(value) => setFormData(prev => ({ ...prev, body: value }))}
                      placeholder="Digite o conteúdo do artigo..."
                      disabled={modalLoading}
                    />
                  </div>
                  {validationErrors.body && (
                    <Text size="sm" className="text-red-500 mt-1">
                      {validationErrors.body}
                    </Text>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <Button
                onClick={closeModal}
                variant="outline"
                disabled={modalLoading}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                disabled={modalLoading || !formData.title.trim() || !formData.body.trim()}
                className="flex items-center gap-2"
                style={{ backgroundColor: BRAND_COLOR }}
              >
                {modalLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Salvando...
                  </>
                ) : (
                  <>
                    <CheckIcon className="h-4 w-4" />
                    {editingArticle ? 'Atualizar' : 'Criar'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Visualização */}
      {showViewModal && viewingArticle && (
        <div className={`fixed inset-0 bg-black bg-opacity-50 flex ${isViewMaximized ? 'items-stretch justify-stretch p-0' : 'items-center justify-center p-4'} z-50`}>
          <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full ${isViewMaximized ? 'max-w-none h-full' : 'max-w-4xl max-h-[90vh]'} flex flex-col`}>
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                {viewingArticle.title}
              </Heading>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setIsViewMaximized(v => !v)}
                  size="sm"
                  variant="outline"
                  className="flex items-center gap-1"
                >
                  {isViewMaximized ? 'Restaurar' : 'Maximizar'}
                </Button>
                <Button
                  onClick={closeViewModal}
                  size="sm"
                  variant="outline"
                  className="flex items-center gap-1"
                >
                  <XMarkIcon className="h-4 w-4" />
                  Fechar
                </Button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              {/* Todos os metadados em uma linha */}
              <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 flex-wrap mb-6">
                {viewingArticle.category && (
                  <div className="flex items-center gap-1">
                    <FolderIcon className="h-4 w-4" />
                    {viewingArticle.category.name}
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <CalendarIcon className="h-4 w-4" />
                  {new Date(viewingArticle.created_at).toLocaleDateString('pt-BR')}
                </div>
                <div className="flex items-center gap-1">
                  <UserIcon className="h-4 w-4" />
                  {userNames[viewingArticle.created_by || ''] || viewingArticle.created_by || 'Usuário'}
                </div>
                {viewingArticle.responsible_id && (
                  <div className="flex items-center gap-1">
                    <UserIcon className="h-4 w-4" />
                    <span className="text-xs text-gray-500">Resp:</span>
                    {userNames[viewingArticle.responsible_id] || viewingArticle.responsible_id || 'Usuário'}
                  </div>
                )}
                {viewingArticle.department && (
                  <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs">
                    {viewingArticle.department.name}
                  </span>
                )}
                <span className={`px-2 py-1 rounded-full text-xs ${
                  viewingArticle.status === 'published' 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : viewingArticle.status === 'draft'
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                }`}>
                  {viewingArticle.status === 'published' ? 'Publicado' : 
                   viewingArticle.status === 'draft' ? 'Rascunho' : 'Arquivado'}
                </span>
                {viewingArticle.tags && viewingArticle.tags.length > 0 && (
                  <>
                    {viewingArticle.tags.map((tag) => (
                      <span
                        key={tag.id}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 rounded-full text-xs"
                      >
                        <TagIcon className="h-3 w-3" />
                        {tag.name}
                      </span>
                    ))}
                  </>
                )}
              </div>

              {/* Conteúdo do artigo */}
              <div 
                className="prose dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: processInternalLinks(viewingArticle.body, articles) }}
              />
            </div>
            
          </div>
        </div>
      )}

      {/* Modal de Versões */}
      {showVersionsModal && versionsArticle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                Histórico: {versionsArticle.title}
              </Heading>
              <div className="flex items-center gap-2">
                {versions.length > 1 && (
                  <Button
                    onClick={openVersionDiffModal}
                    size="sm"
                    variant="outline"
                    className="flex items-center gap-1"
                  >
                    <AdjustmentsHorizontalIcon className="h-4 w-4" />
                    Comparar
                  </Button>
                )}
                <Button
                  onClick={closeVersionsModal}
                  size="sm"
                  variant="outline"
                  className="flex items-center gap-1"
                >
                  <XMarkIcon className="h-4 w-4" />
                  Fechar
                </Button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              {versionsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  <Text className="ml-2">Carregando versões...</Text>
                </div>
              ) : versions.length === 0 ? (
                <div className="text-center py-8">
                  <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <Text className="text-gray-500">Nenhuma versão encontrada</Text>
                </div>
              ) : (
                <div className="space-y-4">
                  {versions.map((version) => (
                    <div key={version.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-1 bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 rounded-full text-xs font-medium">
                            v{version.version_number}
                          </span>
                          <Text size="sm" className="text-gray-500 dark:text-gray-400">
                            {new Date(version.created_at).toLocaleString('pt-BR')}
                          </Text>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={() => openVersionViewModal(version)}
                            size="sm"
                            variant="outline"
                            className="text-primary-600 hover:text-primary-700"
                          >
                            <EyeIcon className="h-4 w-4 mr-1" />
                            Visualizar
                          </Button>
                          <Button
                            onClick={() => handleRestoreVersion(version.version_number)}
                            size="sm"
                            variant="outline"
                            className="text-green-600 hover:text-green-700"
                          >
                            Restaurar
                          </Button>
                        </div>
                      </div>
                      
                      <Text size="sm" className="text-gray-600 dark:text-gray-300 mb-2">
                        {version.change_summary || 'Sem descrição das mudanças'}
                      </Text>
                      
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                        Por: {version.user_name || 'Usuário'}
                      </div>

                      
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Visualização de Versão */}
      {showVersionViewModal && viewingVersion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                Versão {viewingVersion.version_number}: {viewingVersion.title}
              </Heading>
              <Button
                onClick={closeVersionViewModal}
                size="sm"
                variant="outline"
                className="flex items-center gap-1"
              >
                <XMarkIcon className="h-4 w-4" />
                Fechar
              </Button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              {/* Metadados da versão */}
              <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 flex-wrap mb-6">
                <div className="flex items-center gap-1">
                  <CalendarIcon className="h-4 w-4" />
                  {new Date(viewingVersion.created_at).toLocaleString('pt-BR')}
                </div>
                <div className="flex items-center gap-1">
                  <UserIcon className="h-4 w-4" />
                  {viewingVersion.user_name || 'Usuário'}
                </div>
                <span className="px-2 py-1 bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 rounded-full text-xs">
                  v{viewingVersion.version_number}
                </span>
                {viewingVersion.change_summary && (
                  <div className="flex items-center gap-1">
                    <span className="text-gray-600 dark:text-gray-300">
                      {viewingVersion.change_summary}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Conteúdo da versão */}
              <div
                className="prose dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: viewingVersion.body }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de Comparação de Versões */}
      {showVersionDiffModal && versions.length > 0 && (
        <VersionDiff
          versions={versions}
          onClose={closeVersionDiffModal}
        />
      )}

      {/* Helper de Links Internos - Versão Simples */}
      {showInternalLinkHelper && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Links Internos Disponíveis
              </h2>
              <button
                onClick={() => setShowInternalLinkHelper(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-xl"
              >
                ✕
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                <p><strong>Como usar:</strong> Digite [[Nome do Artigo]] no editor para criar links internos</p>
                <p className="mt-1">Exemplo: [[Como Fazer Login]]</p>
              </div>
              
              <div className="space-y-2">
                {articles.slice(0, 20).map((article) => (
                  <div key={article.id} className="flex items-center p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white truncate">
                        {article.title}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {article.category?.name} • {article.status}
                      </p>
                    </div>
                    <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                      [[{article.title}]]
                    </code>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default WikiArtigos
